import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import './FlowLayout.css';
import './ConnectLogin.css';

function ConnectLogin() {
  var navigate = useNavigate();
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [mfaVisible, setMfaVisible] = useState(false);
  var [mfaCode, setMfaCode] = useState('');
  var [authPayload, setAuthPayload] = useState(null);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');

  var API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

  async function handleSignIn() {
    if (!email || !password) return;
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_ENDPOINT}/api/globalentry/authenticate/`, {
        email: email,
        password: password
      });

      localStorage.setItem('userEmail', email);

      if (response.data.test_mode) {
        // Test user flow - auto-trigger MFA
        const mfaResponse = await axios.post(`${API_ENDPOINT}/api/globalentry/mfa/`, {
          mfa_code: '123456',
        });
        localStorage.setItem('token', mfaResponse.data.temp_token || 'dummy-token');
        proceedToRegistration(mfaResponse.data.temp_token || 'dummy-token');
      } else {
        // Real user flow - show MFA input
        setAuthPayload(response.data);
        setMfaVisible(true);
        setLoading(false);
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError('Invalid email or password. Please try again.');
      setLoading(false);
    }
  }

  async function handleVerify() {
    if (mfaCode.length < 6) return;
    setLoading(true);
    setError('');

    try {
      const mfaResponse = await axios.post(`${API_ENDPOINT}/api/globalentry/mfa/`, {
        mfa_url: authPayload.mfa_url,
        idp_session: authPayload.idp_session,
        sp_issuer: authPayload.sp_issuer,
        form_parameters: authPayload.form_parameters,
        authenticity_token: authPayload.authenticity_token,
        mfa_code: mfaCode
      });

      const receivedToken = mfaResponse.data.token || mfaResponse.data.temp_token;
      localStorage.setItem('token', receivedToken);
      proceedToRegistration(receivedToken);
    } catch (err) {
      console.error('MFA Error:', err);
      setError('Invalid verification code. Please try again.');
      setLoading(false);
    }
  }

  function proceedToRegistration(token) {
    var userEmail = localStorage.getItem('userEmail') || email;
    var rawSlots = JSON.parse(localStorage.getItem('selectedSlots') || '{}');
    var locations = JSON.parse(localStorage.getItem('locations') || '[]');
    var times = Array.isArray(rawSlots) ? rawSlots : Object.keys(rawSlots);

    // Convert string blocks to ISO Strings
    const convertToISOFormat = (timeStr) => {
      const [datePart, timePart] = timeStr.split('|');
      let targetDate = new Date(); 

      if (datePart.includes(',')) {
          const year = targetDate.getFullYear();
          targetDate = new Date(`${datePart}, ${year}`);
      } else {
          const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const targetDay = daysOfWeek.indexOf(datePart);
          const currentDay = targetDate.getDay();
          let daysToAdd = targetDay - currentDay;
          if (daysToAdd <= 0) daysToAdd += 7; 
          targetDate.setDate(targetDate.getDate() + daysToAdd);
      }

      const timeMatches = timePart.match(/(\d+)–\d+\s*([AP]M)/i);
      if (!timeMatches) return targetDate.toISOString();

      let hours = parseInt(timeMatches[1], 10);
      const period = timeMatches[2].toUpperCase();

      if (period === 'PM' && hours !== 12) {
          hours += 12;
      }
      if (period === 'AM' && hours === 12) {
          hours = 0;
      }

      targetDate.setHours(hours, 0, 0, 0);
      return targetDate.toISOString();
    };

    const convertedTimes = times.map(timeStr => convertToISOFormat(timeStr));

    axios.post(API_ENDPOINT + '/api/register/', {
      email: userEmail,
      subscribed_locations: locations,
      product: 'AUTOBOOK',
      token: token,
      times: convertedTimes,
    }).then(function(response) {
      window.location.href = response.data.url;
    }).catch(function(error) {
      console.error('Error during registration:', error);
      setError('An error occurred while preparing your booking. Please try again.');
      setLoading(false);
    });
  }

  return (
    <div className="flow-page">
      <Helmet>
        <title>Connect Login.gov</title>
        <meta name="description" content="Sign in with your Login.gov account so Easy Global Entry can book on your behalf." />
      </Helmet>

      {/* Progress Bar */}
      <div className="progress-bar-container" role="progressbar" aria-valuenow={4} aria-valuemin={1} aria-valuemax={5} aria-label="Step 4 of 5: Connect">
        <div className="progress-bar-header">
          <button className="flow-back" onClick={function() { navigate('/calendar'); window.scrollTo(0, 0); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div className="progress-bar-label">Step 4 of 5: Connect</div>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: '80%' }} />
        </div>
      </div>

      {/* Split layout */}
      <div className="flow-layout">
        {/* Left column */}
        <div className="flow-left">
          <h1 className="flow-heading">
            {mfaVisible
              ? "Enter your verification code."
              : "Sign in to Login.gov"
            }
          </h1>
          <p className="flow-subtitle">
            {mfaVisible
              ? "We sent a request to Login.gov. Enter the 6-digit code sent via SMS to your phone to verify your identity. (If you have another form of MFA, you'll need to change it to SMS on your Login.gov account)"
              : "We need your Login.gov credentials to book appointments on your behalf. Your information is sent directly to Login.gov and never stored by us."
            }
          </p>

          {/* Privacy info on left */}
          <div className="connect-trust">
            <div className="connect-trust__item">
              <svg viewBox="0 0 24 24" fill="none" stroke="#2E8540" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>Credentials sent directly to Login.gov</span>
            </div>
            <div className="connect-trust__item">
              <svg viewBox="0 0 24 24" fill="none" stroke="#2E8540" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Encrypted and never stored</span>
            </div>
            <div className="connect-trust__item">
              <svg viewBox="0 0 24 24" fill="none" stroke="#2E8540" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>We never sell your data</span>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="flow-right">
          <div className="panel-card">
            {/* Login.gov header */}
            <div className="connect-gov-header">
              <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span className="connect-gov-header__text">Login.gov</span>
            </div>

            {/* Email */}
            <div className="connect-field">
              <label className="flow-label" htmlFor="login-email">Email Address</label>
              <input
                id="login-email"
                className="connect-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={function(e) { setEmail(e.target.value); }}
                disabled={mfaVisible || loading}
              />
            </div>

            {/* Password */}
            <div className="connect-field">
              <label className="flow-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                className="connect-input"
                type="password"
                placeholder="Enter your Login.gov password"
                value={password}
                onChange={function(e) { setPassword(e.target.value); }}
                disabled={mfaVisible || loading}
                onKeyDown={function(e) { if (e.key === 'Enter') handleSignIn(); }}
              />
            </div>
            
            {error && !mfaVisible && (
              <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</div>
            )}

            {/* Sign in button */}
            <button
              className={'flow-btn-primary' + (mfaVisible ? ' flow-btn-primary--done' : '')}
              onClick={mfaVisible ? undefined : handleSignIn}
              style={{ width: '100%' }}
              disabled={mfaVisible || !email || !password || loading}
            >
              {mfaVisible ? (
                <span className="connect-btn-done">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Signed In
                </span>
              ) : loading ? (
                'Connecting...'
              ) : (
                'Sign In'
              )}
            </button>

            {/* MFA input — slides down after sign in */}
            <div className={'connect-mfa' + (mfaVisible ? ' connect-mfa--visible' : '')}>
              <div className="connect-mfa__inner">
                <div className="connect-mfa__divider"></div>
                <label className="flow-label" htmlFor="mfa-code">Two-Factor Authentication Code</label>
                <input
                  id="mfa-code"
                  className="connect-mfa__input"
                  type="text"
                  inputMode="numeric"
                  maxLength="6"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={function(e) {
                    var val = e.target.value.replace(/[^0-9]/g, '');
                    if (val.length <= 6) setMfaCode(val);
                  }}
                  disabled={loading}
                  onKeyDown={function(e) { if (e.key === 'Enter') handleVerify(); }}
                />
                <p className="connect-mfa__hint">Enter the 6-digit code from your authenticator app.</p>
                
                {error && mfaVisible && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</div>
                )}

                <button
                  className="flow-btn-primary"
                  onClick={handleVerify}
                  disabled={mfaCode.length < 6 || loading}
                  style={{ width: '100%' }}
                >
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </div>
            </div>

            <p className="connect-privacy-link">
              By continuing, you agree to our{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConnectLogin;
